import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const DATE_OPTIONS = [
  { value: 'last7', label: 'Last 7 Days' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'allTime', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

const CALL_OUTCOME_OPTIONS = [
  { value: 'all', label: 'All Outcomes' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'denied', label: 'Denied' },
  { value: 'no_response', label: 'No Response' },
];

const FINAL_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'rto', label: 'RTO' },
];

const NDRFiltersBar = ({ filters, onChange }) => {
  const [dateOpen, setDateOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const dateRef = useRef(null);
  const outcomeRef = useRef(null);
  const statusRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dateRef?.current && !dateRef?.current?.contains(e?.target)) setDateOpen(false);
      if (outcomeRef?.current && !outcomeRef?.current?.contains(e?.target)) setOutcomeOpen(false);
      if (statusRef?.current && !statusRef?.current?.contains(e?.target)) setStatusOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedDateLabel =
    filters?.dateRange === 'custom' && filters?.customFrom && filters?.customTo
      ? `${filters?.customFrom} → ${filters?.customTo}`
      : DATE_OPTIONS?.find((d) => d?.value === filters?.dateRange)?.label || 'Last 7 Days';

  const selectedOutcomeLabel =
    CALL_OUTCOME_OPTIONS?.find((s) => s?.value === filters?.callOutcome)?.label || 'All Outcomes';

  const selectedStatusLabel =
    FINAL_STATUS_OPTIONS?.find((s) => s?.value === filters?.ndrStatus)?.label || 'All Statuses';

  const activeChips = [];
  if (filters?.dateRange && filters?.dateRange !== 'last7') {
    activeChips?.push({ key: 'dateRange', label: selectedDateLabel });
  }
  if (filters?.callOutcome && filters?.callOutcome !== 'all') {
    activeChips?.push({ key: 'callOutcome', label: selectedOutcomeLabel });
  }
  if (filters?.ndrStatus && filters?.ndrStatus !== 'all') {
    activeChips?.push({ key: 'ndrStatus', label: selectedStatusLabel });
  }
  if (filters?.search) {
    activeChips?.push({ key: 'search', label: `"${filters?.search}"` });
  }

  const removeChip = (key) => {
    if (key === 'dateRange') onChange({ ...filters, dateRange: 'last7', customFrom: '', customTo: '' });
    else if (key === 'callOutcome') onChange({ ...filters, callOutcome: 'all' });
    else if (key === 'ndrStatus') onChange({ ...filters, ndrStatus: 'all' });
    else if (key === 'search') onChange({ ...filters, search: '' });
  };

  return (
    <div className="bg-white border-b border-gray-100 px-4 md:px-6 py-3 sticky top-0 z-20 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Range */}
        <div className="relative" ref={dateRef}>
          <button
            onClick={() => setDateOpen(!dateOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 transition-colors min-w-0 sm:min-w-[140px]"
          >
            <Icon name="Calendar" size={15} color="#6b7280" />
            <span className="truncate max-w-[120px]">{selectedDateLabel}</span>
            <Icon name="ChevronDown" size={14} color="#9ca3af" />
          </button>
          {dateOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 w-52 py-1">
              {DATE_OPTIONS?.map((opt) => (
                <button
                  key={opt?.value}
                  onClick={() => {
                    onChange({ ...filters, dateRange: opt?.value });
                    if (opt?.value !== 'custom') setDateOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${
                    filters?.dateRange === opt?.value ? 'text-blue-600 font-semibold bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
              {filters?.dateRange === 'custom' && (
                <div className="px-4 py-2 border-t border-gray-100 flex flex-col gap-2">
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(e) => setCustomFrom(e?.target?.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(e) => setCustomTo(e?.target?.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    disabled={!customFrom || !customTo}
                    onClick={() => {
                      onChange({ ...filters, dateRange: 'custom', customFrom, customTo });
                      setDateOpen(false);
                    }}
                    className="w-full py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold disabled:opacity-40 hover:bg-blue-700 transition-colors"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-0 w-full sm:min-w-[200px] sm:max-w-xs">
          <Icon name="Search" size={15} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by Order ID or Mobile..."
            value={filters?.search}
            onChange={(e) => onChange({ ...filters, search: e?.target?.value })}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all"
          />
        </div>

        {/* Call Outcome */}
        <div className="relative" ref={outcomeRef}>
          <button
            onClick={() => setOutcomeOpen(!outcomeOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 transition-colors min-w-0 sm:min-w-[140px]"
          >
            <Icon name="PhoneCall" size={15} color="#6b7280" />
            <span>{selectedOutcomeLabel}</span>
            <Icon name="ChevronDown" size={14} color="#9ca3af" />
          </button>
          {outcomeOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 w-44 py-1">
              {CALL_OUTCOME_OPTIONS?.map((opt) => (
                <button
                  key={opt?.value}
                  onClick={() => {
                    onChange({ ...filters, callOutcome: opt?.value });
                    setOutcomeOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${
                    filters?.callOutcome === opt?.value ? 'text-blue-600 font-semibold bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Final Order Status */}
        <div className="relative" ref={statusRef}>
          <button
            onClick={() => setStatusOpen(!statusOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 transition-colors min-w-0 sm:min-w-[140px]"
          >
            <Icon name="Filter" size={15} color="#6b7280" />
            <span>{selectedStatusLabel}</span>
            <Icon name="ChevronDown" size={14} color="#9ca3af" />
          </button>
          {statusOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 w-44 py-1">
              {FINAL_STATUS_OPTIONS?.map((opt) => (
                <button
                  key={opt?.value}
                  onClick={() => {
                    onChange({ ...filters, ndrStatus: opt?.value });
                    setStatusOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${
                    filters?.ndrStatus === opt?.value ? 'text-blue-600 font-semibold bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Active Filter Chips */}
      {activeChips?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {activeChips?.map((chip) => (
            <span
              key={chip?.key}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-xs font-medium text-blue-700"
            >
              {chip?.label}
              <button
                onClick={() => removeChip(chip?.key)}
                className="hover:text-blue-900 transition-colors"
              >
                <Icon name="X" size={11} />
              </button>
            </span>
          ))}
          <button
            onClick={() => onChange({ dateRange: 'last7', search: '', ndrStatus: 'all', callOutcome: 'all', customFrom: '', customTo: '' })}
            className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default NDRFiltersBar;
